import React, { useContext, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { AppContext } from 'src/components/app-context';
import ErrorBoundary from '../error-boundary';
import { getAdService } from './services';
import { getAdContextCookie } from './helpers/cookie';
import { IAdContainerProps, IAdContainerWithVisibilityProps } from './types';
import { AdContainerSkeleton, AdLabel } from './styles';
import { adBlockerClassName } from './constants';

const IN_VIEW_THRESHOLD = [0];

interface IAdContainerPropsExtras extends IAdContainerProps {
  serviceName: string;
}

const AdContainer: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IAdContainerPropsExtras>
>> = props => {
  const { t, market } = useContext(AppContext);
  const service = getAdService(props.serviceName);

  return (
    <AdContainerSkeleton
      $placement={props.placement}
      $spacing={props.spacing}
      className={adBlockerClassName}
    >
      <service.container {...props}>
        <AdLabel $align={market.ads.labelAlign}>
          {t('common', 'ads.label')}
        </AdLabel>
      </service.container>
    </AdContainerSkeleton>
  );
};

const AdContainerWithVisibility: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IAdContainerWithVisibilityProps>
>> = props => {
  const [isVisible, setIsVisible] = useState(false);
  const { className, renderWhenInScreen, triggerPoint } = props;
  const { market } = useContext(AppContext);
  const serviceName = getAdContextCookie().service || market.ads.service;

  const onChangeHandler = (inView: boolean) => {
    if (isVisible !== inView) {
      setIsVisible(inView);
    }
  };

  if (
    props.onlyWithServices &&
    Array.isArray(props.onlyWithServices) &&
    !props.onlyWithServices.includes(serviceName)
  ) {
    return null;
  }

  return (
    <InView
      threshold={IN_VIEW_THRESHOLD}
      onChange={onChangeHandler}
      rootMargin={`0px 0px ${triggerPoint}px 0px`}
    >
      {({ ref }) => (
        <div ref={ref} className={className} data-test="AdContainer">
          <AdContainer
            {...props}
            isVisible={renderWhenInScreen ? !!isVisible : true}
            serviceName={serviceName}
          />
        </div>
      )}
    </InView>
  );
};

AdContainerWithVisibility.defaultProps = {
  renderWhenInScreen: true,
  className: undefined,
  onStatusChange: () => {},
  triggerPoint: 300,
};

const AdContainerWithErrorBoundary: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IAdContainerWithVisibilityProps>
>> = props => (
  <ErrorBoundary onError={() => null}>
    <AdContainerWithVisibility {...props} />
  </ErrorBoundary>
);

export default AdContainerWithErrorBoundary;
